import { useState, useEffect, useCallback } from 'react'
import { Search, Plus, ChevronLeft, ChevronRight, Wrench, Eye, Clock } from 'lucide-react'
import api from '@/lib/api'
import RepairDetailModal from './RepairDetailModal'
import CreateRepairModal from './CreateRepairModal'

interface RepairsListTabProps {
  statusFilter?: string
  onStatsChange?: () => void
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  requested: { label: 'Solicitada', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  approved: { label: 'Aprobada', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  pickup_scheduled: { label: 'Recogida prog.', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300' },
  in_transit: { label: 'En tránsito', color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300' },
  received: { label: 'Recibido', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300' },
  diagnosing: { label: 'Diagnosticando', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  quote_sent: { label: 'Presup. enviado', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  quote_approved: { label: 'Presup. aceptado', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  quote_rejected: { label: 'Presup. rechazado', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' },
  waiting_parts: { label: 'Esperando piezas', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  repairing: { label: 'Reparando', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  quality_check: { label: 'Control calidad', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  not_repairable: { label: 'No reparable', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  payment_pending: { label: 'Pago pendiente', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  payment_confirmed: { label: 'Pago confirmado', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  return_shipped: { label: 'Enviado', color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  delivered: { label: 'Entregado', color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  completed: { label: 'Completada', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  cancelled: { label: 'Cancelada', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300' },
}

const REPAIR_TYPE_LABELS: Record<string, string> = {
  screen: 'Pantalla', keyboard: 'Teclado', motherboard: 'Placa base',
  battery: 'Batería', power: 'Carga/Jack', hinge: 'Bisagras',
  casing: 'Carcasa', general: 'Otro', console: 'Consola',
}

// Phase groups for visual styling
const PHASE_PENDING = ['requested', 'approved', 'pickup_scheduled', 'in_transit']
const PHASE_WORKSHOP = ['received', 'diagnosing', 'quote_sent', 'quote_approved', 'waiting_parts', 'repairing', 'quality_check', 'payment_pending', 'payment_confirmed']
const PHASE_FINISHED = ['return_shipped', 'delivered', 'completed']
const PHASE_CLOSED = ['rejected', 'cancelled', 'quote_rejected']
const PHASE_NOT_REPAIRABLE = ['not_repairable']

function getPhaseStyle(status: string): { border: string; bg: string; dimmed: boolean } {
  if (PHASE_PENDING.includes(status)) return { border: 'border-l-amber-400', bg: '', dimmed: false }
  if (PHASE_WORKSHOP.includes(status)) return { border: 'border-l-blue-500', bg: '', dimmed: false }
  if (PHASE_NOT_REPAIRABLE.includes(status)) return { border: 'border-l-red-500', bg: 'bg-red-50/40 dark:bg-red-950/10', dimmed: false }
  if (PHASE_FINISHED.includes(status)) return { border: 'border-l-emerald-400', bg: '', dimmed: true }
  if (PHASE_CLOSED.includes(status)) return { border: 'border-l-slate-300 dark:border-l-slate-600', bg: '', dimmed: true }
  return { border: 'border-l-slate-200', bg: '', dimmed: false }
}

const CUSTOMER_TYPE_COLORS: Record<string, string> = {
  PVP: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
  PVD: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  VIP: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
}

function daysElapsed(dateStr: string): number {
  const created = new Date(dateStr)
  const now = new Date()
  return Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
}

function daysLabel(days: number): { text: string; color: string } {
  if (days === 0) return { text: 'Hoy', color: 'text-emerald-600 dark:text-emerald-400' }
  if (days === 1) return { text: '1d', color: 'text-slate-500' }
  if (days <= 7) return { text: `${days}d`, color: 'text-slate-500' }
  if (days <= 14) return { text: `${days}d`, color: 'text-amber-600 dark:text-amber-400' }
  return { text: `${days}d`, color: 'text-red-600 dark:text-red-400' }
}

export default function RepairsListTab({ statusFilter, onStatsChange }: RepairsListTabProps) {
  const [repairs, setRepairs] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedRepairId, setSelectedRepairId] = useState<number | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const limit = 30

  const loadRepairs = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      if (search) params.set('search', search)
      params.set('limit', String(limit))
      params.set('offset', String(page * limit))

      const res = await api.get(`/repairs/admin?${params}`)
      setRepairs(res.data.repairs || [])
      setTotal(res.data.total || 0)
    } catch (err) {
      console.error('Error loading repairs:', err)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, search, page])

  useEffect(() => { loadRepairs() }, [loadRepairs])
  useEffect(() => { setPage(0) }, [statusFilter, search])

  const totalPages = Math.ceil(total / limit)

  const handleRepairUpdated = () => {
    loadRepairs()
    onStatsChange?.()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="px-6 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por código, nombre, email, modelo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        {/* Legend */}
        <div className="hidden lg:flex items-center gap-3 text-[11px] text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />Pendiente</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Taller</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />No reparable</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" />Finalizada</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" />Cerrada</span>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva reparación
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
            <tr className="text-left text-slate-500 dark:text-slate-400">
              <th className="pl-2 pr-2 py-3 font-medium w-1"></th>
              <th className="px-4 py-3 font-medium">Código</th>
              <th className="px-4 py-3 font-medium">Cliente</th>
              <th className="px-4 py-3 font-medium">Dispositivo</th>
              <th className="px-4 py-3 font-medium">Tipo</th>
              <th className="px-4 py-3 font-medium">Estado</th>
              <th className="px-4 py-3 font-medium">Total</th>
              <th className="px-4 py-3 font-medium text-center">Días</th>
              <th className="px-4 py-3 font-medium w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
            {loading ? (
              <tr><td colSpan={9} className="px-6 py-12 text-center text-slate-400">Cargando...</td></tr>
            ) : repairs.length === 0 ? (
              <tr><td colSpan={9} className="px-6 py-12 text-center text-slate-400">No hay reparaciones</td></tr>
            ) : repairs.map((r) => {
              const st = STATUS_LABELS[r.status] || { label: r.status, color: 'bg-slate-100 text-slate-600' }
              const phase = getPhaseStyle(r.status)
              const days = r.created_at ? daysElapsed(r.created_at) : 0
              const dl = daysLabel(days)
              const isFinished = phase.dimmed
              const ctColor = CUSTOMER_TYPE_COLORS[r.customer_type] || CUSTOMER_TYPE_COLORS.PVP

              return (
                <tr
                  key={r.id}
                  className={`border-l-[3px] ${phase.border} ${phase.bg} hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors ${isFinished ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedRepairId(r.id)}
                >
                  <td className="w-1"></td>
                  <td className="px-4 py-2.5">
                    <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400 text-xs">{r.code}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-slate-900 dark:text-white text-sm leading-tight">{r.customer_name}</span>
                      <span className={`px-1.5 py-0 rounded text-[10px] font-bold leading-relaxed ${ctColor}`}>{r.customer_type}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 truncate max-w-[200px]">{r.customer_email}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-900 dark:text-white text-sm">{r.device_brand === 'Otro' ? r.device_brand_other : r.device_brand}</div>
                    <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{r.device_model || '-'}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-slate-600 dark:text-slate-400 text-xs">{REPAIR_TYPE_LABELS[r.repair_type] || r.repair_type || '-'}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${st.color}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {r.quote_total > 0 ? (
                      <span className="font-semibold text-slate-900 dark:text-white text-sm">{r.quote_total.toFixed(2)} €</span>
                    ) : (
                      <span className="text-slate-300 dark:text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1" title={r.created_at ? new Date(r.created_at).toLocaleDateString('es-ES') : ''}>
                      <Clock className={`w-3 h-3 ${dl.color}`} />
                      <span className={`text-xs font-medium ${dl.color}`}>{dl.text}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <button className="p-1 text-slate-400 hover:text-indigo-600">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <span className="text-sm text-slate-500">{total} reparaciones</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-slate-600 dark:text-slate-400">{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-600 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-700">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedRepairId && (
        <RepairDetailModal
          repairId={selectedRepairId}
          onClose={() => setSelectedRepairId(null)}
          onUpdated={handleRepairUpdated}
        />
      )}

      {showCreateModal && (
        <CreateRepairModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); handleRepairUpdated() }}
        />
      )}
    </div>
  )
}
